import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { teamService, ROLES } from '../services/teamService';
import { TopBar } from '../components/common/TopBar';
import { Modal } from '../components/common/Modal';
import { LoginButton } from '../components/common/LoginButton';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import './Teams.css';

// ── Role Picker ────────────────────────────────────────────
const RolePicker = ({ availableRoles, selectedRole, onSelect }) => (
  <div className="role-picker">
    {ROLES.map((r) => {
      const available = availableRoles.find((a) => a.value === r.value);
      const isSelected = selectedRole === r.value;
      return (
        <button
          key={r.value}
          className={`role-picker__card ${isSelected ? 'role-picker__card--selected' : ''} ${!available ? 'role-picker__card--taken' : ''}`}
          onClick={() => available && onSelect(r.value)}
          disabled={!available}
          id={`role-pick-${r.value}`}
        >
          <span className="role-picker__icon">{r.icon}</span>
          <span className="role-picker__label">{r.label}</span>
          <span className="role-picker__desc">{available ? r.desc : 'Già occupato'}</span>
        </button>
      );
    })}
  </div>
);

// ── Teams Page ─────────────────────────────────────────────
const Teams = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create modal state
  const [createModal, setCreate] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [createRole, setCreateRole] = useState('');
  const [createStep, setCreateStep] = useState('name'); // 'name' | 'role'

  // Join modal state
  const [joinModal, setJoin] = useState(false);
  const [inviteCode, setInvite] = useState('');
  const [joinRole, setJoinRole] = useState('');
  const [joinStep, setJoinStep] = useState('code'); // 'code' | 'role'
  const [joinTeamPreview, setJoinTeamPreview] = useState(null); // team preview after code lookup

  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    // Load teams by createdBy and also by memberIds
    const [created, joined] = await Promise.all([
      teamService.getTeamsByUser(currentUser.uid),
      teamService.getAllTeamsByUser(currentUser.uid),
    ]);
    // Merge, deduplicate by id
    const map = {};
    [...created, ...joined].forEach((t) => { map[t.id] = t; });
    setTeams(Object.values(map));
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  // ── Handlers: Create ──────────────────────────────────────
  const handleCreateNext = () => {
    if (!teamName.trim()) return setError('Inserisci un nome per la squadra');
    setError('');
    setCreateStep('role');
  };

  const handleCreate = async () => {
    if (!createRole) return setError('Scegli il tuo ruolo nella squadra');
    setError('');
    setActionLoading(true);
    try {
      const id = await teamService.createTeam(
        teamName.trim(),
        currentUser.uid,
        currentUser.displayName || 'Anonimo',
        createRole,
      );
      resetCreate();
      navigate(`/teams/${id}`);
    } catch (e) {
      setError(e.message);
    }
    setActionLoading(false);
  };

  const resetCreate = () => {
    setCreate(false);
    setTeamName('');
    setCreateRole('');
    setCreateStep('name');
    setError('');
  };

  // ── Handlers: Join ────────────────────────────────────────
  const handleCodeLookup = async () => {
    if (!inviteCode.trim()) return setError('Inserisci il codice invito');
    setError('');
    setActionLoading(true);
    try {
      // Preview: cerca il team senza unirsi ancora
      const q = query(collection(db, 'teams'), where('inviteCode', '==', inviteCode.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error('Codice squadra non trovato');
      const teamData = { id: snap.docs[0].id, ...snap.docs[0].data() };
      if (teamData.members.find((m) => m.userId === currentUser.uid)) {
        // Already in → just navigate
        resetJoin();
        navigate(`/teams/${teamData.id}`);
        return;
      }
      if (teamData.members.length >= 3) throw new Error('La squadra è già completa');
      setJoinTeamPreview(teamData);
      setJoinStep('role');
    } catch (e) {
      setError(e.message);
    }
    setActionLoading(false);
  };

  const handleJoin = async () => {
    if (!joinRole) return setError('Scegli il tuo ruolo nella squadra');
    setError('');
    setActionLoading(true);
    try {
      const team = await teamService.joinTeamByCode(
        inviteCode.trim(),
        currentUser.uid,
        currentUser.displayName || 'Anonimo',
        joinRole,
      );
      resetJoin();
      navigate(`/teams/${team.id}`);
    } catch (e) {
      setError(e.message);
    }
    setActionLoading(false);
  };

  const resetJoin = () => {
    setJoin(false);
    setInvite('');
    setJoinRole('');
    setJoinStep('code');
    setJoinTeamPreview(null);
    setError('');
  };

  // ── Render: Not logged in ─────────────────────────────────
  if (!currentUser) {
    return (
      <div className="teams-page">
        <TopBar title="Squadre" />
        <div className="teams-empty">
          <span className="teams-empty__icon">🏆</span>
          <p>Devi effettuare il login per gestire le squadre.</p>
          <LoginButton className="start-btn">Accedi</LoginButton>
        </div>
      </div>
    );
  }

  // ── Render: Main ──────────────────────────────────────────
  return (
    <div className="teams-page">
      <TopBar title="Squadre" onBack={() => navigate('/')} />

      <div className="teams-content">
        {loading ? (
          <LoadingSpinner />
        ) : teams.length === 0 ? (
          <div className="teams-empty">
            <span className="teams-empty__icon">🏆</span>
            <p>Non sei ancora in nessuna squadra.</p>
          </div>
        ) : (
          <div className="teams-list">
            {teams.map((t) => {
              const myMember = t.members?.find((m) => m.userId === currentUser.uid);
              const myRole = myMember?.role;
              const roleInfo = ROLES.find((r) => r.value === myRole);
              return (
                <button
                  key={t.id}
                  className="team-card"
                  onClick={() => navigate(`/teams/${t.id}`)}
                  id={`team-${t.id}`}
                >
                  <div className="team-card__avatar">{t.name.charAt(0).toUpperCase()}</div>
                  <div className="team-card__info">
                    <h2 className="team-card__name">{t.name}</h2>
                    <p className="team-card__members">
                      {t.members?.length ?? 0}/3 membri
                      {roleInfo ? ` · ${roleInfo.icon} ${roleInfo.label}` : ''}
                    </p>
                  </div>
                  <span className="team-card__arrow">›</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="teams-actions">
          <button
            className="action-btn action-btn--primary"
            onClick={() => { setCreate(true); setError(''); }}
            id="btn-create-team"
          >
            + Crea Squadra
          </button>
          <button
            className="action-btn action-btn--secondary"
            onClick={() => { setJoin(true); setError(''); }}
            id="btn-join-team"
          >
            Entra con codice
          </button>
        </div>
      </div>

      {/* ── Create Modal ─────────────────────────────────── */}
      <Modal isOpen={createModal} onClose={resetCreate} title="Crea Squadra">
        <div className="teams-modal-body">
          {createStep === 'name' ? (
            <>
              <label className="pm-label">Nome della squadra</label>
              <input
                className="pm-input"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateNext()}
                placeholder="Es. I Campioni"
                id="input-team-name"
              />
              {error && <p className="teams-modal-error">{error}</p>}
              <button className="pm-save-btn" onClick={handleCreateNext} id="btn-create-next">
                Avanti →
              </button>
            </>
          ) : (
            <>
              <p className="teams-modal-subtitle">
                Scegli il tuo ruolo permanente in <strong>{teamName}</strong>
              </p>
              <RolePicker
                availableRoles={ROLES}
                selectedRole={createRole}
                onSelect={setCreateRole}
              />
              {error && <p className="teams-modal-error">{error}</p>}
              <div className="teams-modal-actions">
                <button
                  className="back-link"
                  onClick={() => { setCreateStep('name'); setError(''); }}
                >
                  ← Indietro
                </button>
                <button
                  className="pm-save-btn"
                  onClick={handleCreate}
                  disabled={actionLoading || !createRole}
                  id="btn-confirm-create"
                >
                  {actionLoading ? 'Creazione…' : 'Crea Squadra'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ── Join Modal ───────────────────────────────────── */}
      <Modal isOpen={joinModal} onClose={resetJoin} title="Entra in una Squadra">
        <div className="teams-modal-body">
          {joinStep === 'code' ? (
            <>
              <label className="pm-label">Codice invito (6 lettere)</label>
              <input
                className="pm-input teams-code-input"
                value={inviteCode}
                onChange={(e) => setInvite(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleCodeLookup()}
                placeholder="ABCDEF"
                maxLength={6}
                id="input-invite-code"
              />
              {error && <p className="teams-modal-error">{error}</p>}
              <button
                className="pm-save-btn"
                onClick={handleCodeLookup}
                disabled={actionLoading}
                id="btn-lookup-code"
              >
                {actionLoading ? 'Ricerca…' : 'Cerca Squadra →'}
              </button>
            </>
          ) : (
            <>
              <p className="teams-modal-subtitle">
                Unisciti a <strong>{joinTeamPreview?.name}</strong>.<br />
                Scegli il tuo ruolo permanente:
              </p>
              <RolePicker
                availableRoles={teamService.getAvailableRoles(joinTeamPreview)}
                selectedRole={joinRole}
                onSelect={setJoinRole}
              />
              {error && <p className="teams-modal-error">{error}</p>}
              <div className="teams-modal-actions">
                <button
                  className="back-link"
                  onClick={() => { setJoinStep('code'); setError(''); }}
                >
                  ← Indietro
                </button>
                <button
                  className="pm-save-btn"
                  onClick={handleJoin}
                  disabled={actionLoading || !joinRole}
                  id="btn-confirm-join"
                >
                  {actionLoading ? 'Ingresso…' : 'Entra nella Squadra'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Teams;
